import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { Typography, Tooltip, Chip, Button } from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import LazyLoad from 'react-lazyload';

// CONSTANTS
import {
  ROUTE_DISCOVER_APP,
  ROUTE_PROFILE_SUBSCRIPTION_DETAIL,
  ROUTE_SUBSCRIPTION,
} from '@/constants/routes';

// UTILITY
import { updateUrl } from '@/utility/common';
import { getLink, routeEnhance } from '@/utility/routes';
import { analyticsInternalLinks, externalLink, getExternalUrlParams } from '@/utility/analytics';
import { formatPrice } from '@/utility/currency';

// COMPONENTS
import Link from '@/components/Link';
import MBIcon from '@/components/Icons/MBIcon';
import ReactHtml from '@/components/ReactHtml';

// STYLES
import { AppCardContainer } from '../styles';

const AppCard = props => {
  const {
    data,
    className,
    location,
    LinkProps,
    children,
    hideControls,
    showSubscribedMarker,
    btnProps,
    type,
  } = props;
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { isSimpleView, partner, partnerSettings, isIframe, unique_id, anonymousId } = useSelector(
    store => ({
      isSimpleView: store.app.isSimpleView,
      partner: store.partner.data,
      partnerSettings: store.partner.data?.settings || {},
      statusesLoadings: store.appDetails.loadings.statusesLoadings,
      isIframe: store.app.isIframe,
      unique_id: store.auth.unique_id,
      anonymousId: store.auth.anonymousId,
    }),
  );

  const externalUrlParams = getExternalUrlParams({ unique_id: unique_id || anonymousId });
  const href = updateUrl(data.url, externalUrlParams);

  const link = getLink(ROUTE_DISCOVER_APP, { slug: data.unique_slug });

  const hasPartner = !!partner.slug;
  const isSubscribed = data.user_subscribed;
  const hasDealPrice = !!data.deal_price || data.deal_price === 0;
  // const isFree = !!data.price && Math.ceil(data.price) === 0;

  const desc =
    data.desc?.trim() ||
    (data?.super_short_desc?.length ? data.super_short_desc?.trim() : data?.short_desc?.trim()) ||
    '';
  const description = desc.length > 100 ? `${desc.substring(0, 90)}...` : desc;

  let useSubscription = true;

  if (hasPartner) {
    useSubscription = !!partnerSettings.use_authorizing && !!partnerSettings.use_subscription;
  }

  const subscriptionIsAvailable = useSubscription && data.has_subscription && !isSimpleView;
  const hasPurchaseMarker = !!(data.has_subscription && !isSubscribed && useSubscription);
  const hasLiveTvMarker = !!data.has_live_tv;
  const hasTrialMarker = !!data.has_trial;
  const hasPlanMarker = !!data.plan?.name;
  const hasSubscribedMarker = !!data.user_subscription;
  // const hasFreeMarker = (!hasDealPrice && isFree);
  const hasMarkers = hasPurchaseMarker || hasLiveTvMarker || hasTrialMarker || hasPlanMarker;
  // const hasMarkers = hasPurchaseMarker || hasLiveTvMarker || hasTrialMarker || hasFreeMarker;
  const isPromotionsType = type === 'promotions';

  const getPrice = (price, isAnnual = false) => {
    return price && Math.ceil(price) !== 0
      ? t(`common.${isAnnual ? 'costYear' : 'costMonth'}`, { cost: formatPrice(price) })
      : t('common.free');
  };

  const handleVisitSite = () => {
    const properties = {
      location: 'app hover',
      href,
      app_id: data.id,
      type: 'app',
      service_name: data.name,
      is_affiliate: !!data.is_affiliate,
      partner: partner?.partner_slug,
      partner_id: partner?.partner_id,
    };

    externalLink(properties);
  };

  function handleSubscribeOrManage() {
    const isUnsubscribedInIframe = isIframe && !isSubscribed;
    const routeEnhanced = routeEnhance(
      getLink(isSubscribed ? ROUTE_PROFILE_SUBSCRIPTION_DETAIL : ROUTE_SUBSCRIPTION, {
        slug: data.unique_slug,
      }),
      { state: { location }, withoutIframe: isUnsubscribedInIframe },
    );

    analyticsInternalLinks({
      unique_id: routeEnhanced.state.unique_id,
      from_page_path: routeEnhanced.state.from_page_path,
      from_search_params: routeEnhanced.state.from_search_params,
      to_page_path: routeEnhanced.pathname,
      to_search_params: routeEnhanced.search,
      partner_id: routeEnhanced.state.partner_id,
      location: routeEnhanced.state.location,
    });

    if (isUnsubscribedInIframe) {
      window.open(
        `${routeEnhanced.pathname}?${routeEnhanced.search}`,
        '_blank',
        'noopener,noreferrer',
      );
    } else {
      navigate(routeEnhanced);
    }
  }

  const img = (
    <img
      src={data.thumbnail || `https://dummyimage.com/200X80/FFFFFF/000000/?text=${data.name}`}
      alt={data.name}
    />
  );

  return (
    <AppCardContainer className={clsx('app-card', `app-card--type-${type}`, className, {})}>
      {data.has_super_deal && (
        <span className="marker marker--el app-card__marker-deal">
          {t('common.deal').toUpperCase()}
        </span>
      )}
      {hasMarkers && (
        <div className="app-card__markers">
          {hasPurchaseMarker && (
            <Tooltip title={<p className="p4">{t('tooltip.canBePurchased')}</p>} placement="top">
              <i className="app-card__marker-live-tv cursor cursor--pointer">
                <MBIcon color="primary" fontSize="inherit" />
              </i>
            </Tooltip>
          )}
          {hasLiveTvMarker && (
            <Typography className="p4" color="primary">
              {t('common.liveTV')}
            </Typography>
          )}
          {hasTrialMarker && (
            <Chip label={t('common.freeTrial')} color="secondary" className="app-card__chip" />
          )}
          {hasSubscribedMarker && showSubscribedMarker && (
            <Chip
              label={t('common.subscribed')}
              color="secondary"
              className="app-card__chip warning"
            />
          )}
          {hasPlanMarker && (
            <Chip label={data.plan.name} color="secondary" className="app-card__chip" />
          )}
          {/* {hasFreeMarker && <Chip label={t('common.free')} color="secondary" />} */}
        </div>
      )}

      <div className="app-card__content">
        <div className="app-card__logo">{isIframe ? img : <LazyLoad once>{img}</LazyLoad>}</div>
        <Typography className="m-t-15 app-card__title" variant="h5" align="center" noWrap>
          {data.name}
        </Typography>
        <div
          className={clsx('app-card__price-container', 'p5', {
            'app-card__price-container--with-deal': hasDealPrice,
          })}
        >
          <span className="app-card__price-container__cost">
            {getPrice(data.price, !!data.is_annual)}
          </span>
          {hasDealPrice && (
            <span>
              {getPrice(data.deal_price, !!data.is_annual)}{' '}
              {isPromotionsType && data.deal_price_duration_text}
            </span>
          )}
        </div>
        {!!data.rating && (
          <div className="app-card__rating">
            <Rating value={data.rating / 20} precision={0.1} readOnly />
          </div>
        )}
        <div className="app-card__description m-t-15">
          <ReactHtml className="markdown p5">{description}</ReactHtml>
        </div>
        {!hideControls && (
          <>
            {subscriptionIsAvailable ? (
              <Button
                className="m-t-15 app-card__button"
                fullWidth
                variant="outlined"
                color="primary"
                size="medium"
                onClick={handleSubscribeOrManage}
                startIcon={!isSubscribed && <i className="icon-mb-icon-2" />}
                {...btnProps}
              >
                {isSubscribed ? t('buttons.manage') : t('buttons.subscribe')}
              </Button>
            ) : (
              <Button
                className="m-t-15 app-card__button"
                href={href}
                target="_blank"
                variant="outlined"
                color="primary"
                size="medium"
                fullWidth
                onClick={handleVisitSite}
                {...btnProps}
              >
                {data.external_button_text || t('buttons.visitSite')}
              </Button>
            )}
          </>
        )}
        {children}
      </div>
      <Link className="app-card__link" to={link} location={location} {...LinkProps} />
    </AppCardContainer>
  );
};

AppCard.defaultProps = {
  className: '',
  location: '',
  LinkProps: {},
  hideControls: false,
  showSubscribedMarker: false,
  btnProps: {},
  type: 'default',
};

AppCard.propTypes = {
  data: PropTypes.object.isRequired,
  className: PropTypes.string,
  location: PropTypes.string,
  LinkProps: PropTypes.object,
  hideControls: PropTypes.bool,
  children: PropTypes.node,
  showSubscribedMarker: PropTypes.bool,
  btnProps: PropTypes.object,
  type: PropTypes.oneOf(['default', 'promotions']),
};

export default React.memo(AppCard);
